import { Card } from '@citizen-walk/shared-ui';
import { useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { DistrictMiscPanel } from './DistrictMiscPanel';
import { DistrictPhotoPrivacyPanel } from './DistrictPhotoPrivacyPanel';
import { DistrictPhotoSettingsPanel } from './DistrictPhotoSettingsPanel';
import { DistrictSubPageHeader } from './DistrictSubPageHeader';
import { useDistrict } from './useDistricts';

/**
 * Sprint BG-Menu (第 3 段、DistrictDetailPage 分割): その他設定ページ。
 *
 * `/districts/:id/misc` に DistrictMiscPanel (installPromptEnabled / surveyUrl /
 * allowPostingWithoutSession / analysisSiteUrl / mapDownloadPolicy) を分離。
 */
export function DistrictMiscPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const state = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();

  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? '';

  if (state.status === 'loading') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">読込中…</p>
      </Card>
    );
  }
  if (state.status === 'not-found') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-700">
          地区が見つかりません (id: <code>{districtId}</code>)。
        </p>
      </Card>
    );
  }
  if (state.status === 'error') {
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
      </Card>
    );
  }

  return (
    <>
      <DistrictSubPageHeader
        districtId={state.district.id}
        districtName={state.district.name}
        activeTo={`/districts/${state.district.id}/misc`}
      />
      <div className="grid grid-cols-1 gap-4">
        <Card padding="md">
          <h2 className="text-base font-bold text-slate-900 mb-1">その他設定</h2>
          <p className="text-xs text-slate-600">
            インストール促し / 外部アンケート URL / セッション無し投稿許可 / 分析サイト URL /
            地図ダウンロード方針 / 投稿画像の画質・最大サイズ /
            写真のプライバシー（顔・ナンバーの自動ぼかし） などの細かい設定。
            （公開状態（強い停止）は「概要」ページに移動しました。）
          </p>
        </Card>
        <DistrictMiscPanel district={state.district} uid={uid} canEdit={isMaster} />
        <DistrictPhotoSettingsPanel district={state.district} uid={uid} canEdit={isMaster} />
        {/* docs/28 §10-4: 写真のプライバシー (自動ぼかし・ぼかし前の写真の保存)。地区管理者も編集可。 */}
        <DistrictPhotoPrivacyPanel district={state.district} uid={uid} isMaster={isMaster} />
      </div>
    </>
  );
}
